import { SHOPIFY_CONFIG, SHOPIFY_GRAPHQL_URL } from '../config/shopify';
import { recordShopifyCheckoutHandoff } from './bookService';

// How long checkout waits for the handoff record before leaving for Shopify anyway.
const CHECKOUT_HANDOFF_WAIT_MS = 2000;
// How long a Shopify product lookup may take before it counts as failed.
const PRODUCT_FETCH_TIMEOUT_MS = 8000;

function collectBookIdsForCheckoutHandoff(checkoutData) {
  const ids = new Set();
  if (checkoutData.bookIds != null && checkoutData.bookIds !== '') {
    const raw = Array.isArray(checkoutData.bookIds)
      ? checkoutData.bookIds.join(',')
      : String(checkoutData.bookIds);
    raw
      .split(',')
      .map((s) => s.trim())
      .filter(Boolean)
      .forEach((id) => ids.add(id));
  }
  if (checkoutData.bookId != null && checkoutData.bookId !== '') {
    ids.add(String(checkoutData.bookId).trim());
  }
  return [...ids];
}

// GraphQL query to fetch specific products by ID with discount support
const FETCH_SPECIFIC_PRODUCTS_QUERY = `
  query {
    bookProduct: product(id: "gid://shopify/Product/9314844639480") {
      id
      title
      description
      priceRange {
        minVariantPrice {
          amount
          currencyCode
        }
      }
      images(first: 1) {
        edges {
          node {
            url
            altText
          }
        }
      }
      variants(first: 1) {
        edges {
          node {
            id
            title
            price {
              amount
              currencyCode
            }
            compareAtPrice {
              amount
              currencyCode
            }
            availableForSale
          }
        }
      }
    }
    kitProduct: product(id: "gid://shopify/Product/9242881261816") {
      id
      title
      description
      priceRange {
        minVariantPrice {
          amount
          currencyCode
        }
      }
      images(first: 1) {
        edges {
          node {
            url
            altText
          }
        }
      }
      variants(first: 1) {
        edges {
          node {
            id
            title
            price {
              amount
              currencyCode
            }
            compareAtPrice {
              amount
              currencyCode
            }
            availableForSale
          }
        }
      }
    }
  }
`;

// GraphQL query for static “plans” used in ChoosePlan
export const fetchShopifyProducts = async () => {
  try {
    const response = await fetch(SHOPIFY_GRAPHQL_URL, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-Shopify-Storefront-Access-Token': SHOPIFY_CONFIG.storefrontAccessToken,
      },
      body: JSON.stringify({ 
        query: FETCH_SPECIFIC_PRODUCTS_QUERY 
      }),
    });

    const data = await response.json();
    
    console.log('️ Shopify Products Response:', data);
    
    if (data.errors) {
      console.error(' Shopify API Error:', data.errors);
      throw new Error(data.errors[0]?.message || 'Failed to fetch products');
    }

    const { bookProduct, kitProduct } = data.data;

    if (!bookProduct || !kitProduct) {
      throw new Error('Required products not found in Shopify');
    }

    // Get the first variant from each product
    const bookVariant = bookProduct.variants.edges[0]?.node;
    const kitVariant = kitProduct.variants.edges[0]?.node;

    if (!bookVariant || !kitVariant) {
      throw new Error('Product variants not found');
    }

    const basePrice = parseFloat(bookVariant.price.amount);
    const baseCompareAtPrice = bookVariant.compareAtPrice 
      ? parseFloat(bookVariant.compareAtPrice.amount) 
      : null;

    const kitPrice = parseFloat(kitVariant.price.amount);
    const kitCompareAtPrice = kitVariant.compareAtPrice 
      ? parseFloat(kitVariant.compareAtPrice.amount) 
      : null;

    // Helper function to calculate discount
    const calculateDiscount = (originalPrice, discountedPrice) => {
      if (!originalPrice || originalPrice <= discountedPrice) return null;
      return Math.round(((originalPrice - discountedPrice) / originalPrice) * 100);
    };

    // Transform into plan structure with quantities
    const plans = [
      {
        id: 'solo-creation',
        productId: bookProduct.id,
        name: 'Solo Creation',
        description: 'One custom coloring book',
        highLightTag: 0,
        highLightText: '',
        planPricingData: {
          price: basePrice,
          originalPrice: baseCompareAtPrice,
          discount: calculateDiscount(baseCompareAtPrice, basePrice),
          priceDescription: 'Select Plan',
        },
        features: [
          { name: '1 Custom Coloring Book' },
          { name: 'High-Quality Printing' },
          { name: 'Personalized Content' }
        ],
        shopifyProductId: bookProduct.id,
        shopifyVariantId: bookVariant.id,
        quantity: 1,
        offerType: 'SOLO',
        image: bookProduct.images.edges[0]?.node?.url || null,
      },
      {
        id: 'together-set',
        productId: bookProduct.id,
        name: 'Together Set',
        description: 'Two custom coloring books',
        highLightTag: 1,
        highLightText: 'Most Popular',
        planPricingData: {
          price: basePrice * 2,
          originalPrice: baseCompareAtPrice ? baseCompareAtPrice * 2 : null,
          discount: calculateDiscount(baseCompareAtPrice ? baseCompareAtPrice * 2 : null, basePrice * 2),
          priceDescription: 'Select Plan',
        },
        features: [
          { name: '2 Custom Coloring Books' },
          { name: 'High-Quality Printing' },
          { name: 'Personalized Content' },
          { name: 'Buy 2 Save More' }
        ],
        shopifyProductId: bookProduct.id,
        shopifyVariantId: bookVariant.id,
        quantity: 2,
        offerType: 'TOGETHER',
        image: bookProduct.images.edges[0]?.node?.url || null,
      },
      {
        id: 'family-pack',
        productId: bookProduct.id,
        name: 'Family Pack',
        description: 'Three custom coloring books',
        highLightTag: 0,
        highLightText: '',
        planPricingData: {
          price: basePrice * 3,
          originalPrice: baseCompareAtPrice ? baseCompareAtPrice * 3 : null,
          discount: calculateDiscount(baseCompareAtPrice ? baseCompareAtPrice * 3 : null, basePrice * 3),
          priceDescription: 'Select Plan',
        },
        features: [
          { name: '3 Custom Coloring Books' },
          { name: 'High-Quality Printing' },
          { name: 'Personalized Content' },
          { name: 'Buy 3 Save Most' }
        ],
        shopifyProductId: bookProduct.id,
        shopifyVariantId: bookVariant.id,
        quantity: 3,
        offerType: 'FAMILY',
        image: bookProduct.images.edges[0]?.node?.url || null,
      },
      {
        id: 'connection-kit',
        productId: kitProduct.id,
        name: 'Connection Kit',
        description: 'Connection kit for enhanced experience',
        highLightTag: 0,
        highLightText: '',
        planPricingData: {
          price: kitPrice,
          originalPrice: kitCompareAtPrice,
          discount: calculateDiscount(kitCompareAtPrice, kitPrice),
          priceDescription: 'Select Plan',
        },
        features: [
          { name: 'Complete Connection Kit' },
          { name: 'Premium Quality' }
        ],
        shopifyProductId: kitProduct.id,
        shopifyVariantId: kitVariant.id,
        quantity: 1,
        offerType: 'CONNECTION_KIT',
        image: kitProduct.images.edges[0]?.node?.url || null,
      }
    ];

    console.log(' Transformed Plans:', plans);
    return plans;

  } catch (error) {
    console.error(' Error fetching Shopify products:', error);
    throw error;
  }
};

/**
 * Redirects to bridge page with checkout data
 * Now supports multi-book checkout with arrays
 */
export const redirectToBridgePage = async (checkoutData, forCart = false) => {
  if (!forCart) {
    const bookIds = collectBookIdsForCheckoutHandoff(checkoutData);
    if (bookIds.length > 0) {
      try {
        // Nice to have, so a slow API never holds up checkout.
        await Promise.race([
          recordShopifyCheckoutHandoff(bookIds),
          new Promise((resolve) => setTimeout(resolve, CHECKOUT_HANDOFF_WAIT_MS)),
        ]);
      } catch (err) {
        console.warn('[Shopify checkout] Could not record handoff (non-blocking):', err);
      }
    }
  }

  const SHOPIFY_DOMAIN = `www.${process.env.REACT_APP_COOKIE_URL}`;
  
  // Build query parameters
  const params = new URLSearchParams();
  
  // Check if this is multi-book checkout (has bookIds with commas)
  const isMultiBook = checkoutData.bookIds && checkoutData.bookIds.includes(',');
  
  if (isMultiBook) {
    // Multi-book checkout - add array parameters
    console.log('🛒 Building multi-book checkout URL');
    
    params.append('bookIds', checkoutData.bookIds);
    params.append('quantities', checkoutData.quantities);
    params.append('titles', checkoutData.titles);
    params.append('variantIds', checkoutData.variantIds);
    params.append('versions', checkoutData.versions);
    params.append('recipients', checkoutData.recipients);
    params.append('previewUrls', checkoutData.previewUrls);
    params.append('customerId', checkoutData.customerId);
    
    // NEW: Add tag if present
    if (checkoutData.tag) {
      params.append('tag', checkoutData.tag);
      console.log(`Adding tag metadata: ${checkoutData.tag}`);
    }
    
  } else {
    // Single book checkout (legacy support)
    console.log('📦 Building single-book checkout URL');
    
    // For Connection Kit or single book
    params.append('variantId', checkoutData.variantId);
    params.append('quantity', checkoutData.quantity.toString());
    
    // Only add book-specific properties if NOT a Connection Kit
    if (checkoutData.offerType !== 'CONNECTION_KIT' && checkoutData.bookId) {
      params.append('bookIds', checkoutData.bookId); // Single book as "array"
      params.append('quantities', checkoutData.quantity.toString());
      params.append('titles', checkoutData.title || 'Custom Coloring Book');
      params.append('variantIds', checkoutData.variantId);
      params.append('versions', (checkoutData.version || 1).toString());
      params.append('customerId', checkoutData.customerId);
      
      if (checkoutData.recipient) {
        params.append('recipients', checkoutData.recipient);
      }
      if (checkoutData.previewUrl) {
        params.append('previewUrls', checkoutData.previewUrl);
      }
      
      // NEW: Add tag if present
      if (checkoutData.tag) {
        params.append('tag', checkoutData.tag);
        console.log(`🏷️ Adding tag metadata: ${checkoutData.tag}`);
      }
    }
  }

  // Add email if present (applies to all checkout flows)
  if (checkoutData.email) {
    params.append('email', checkoutData.email);
  }

  const pageName = !forCart ? 'secure-checkout' : 'add-to-cart';
  
  // Build full URL
  const bridgeUrl = `https://${SHOPIFY_DOMAIN}/pages/${pageName}?${params.toString()}`;
  
  console.log('🔗 Opening bridge page:', {
    isMultiBook,
    bookCount: isMultiBook ? checkoutData.bookIds.split(',').length : 1,
    hasTag: !!checkoutData.tag,
    tag: checkoutData.tag || 'none',
    url: bridgeUrl
  });
  
  // Same tab. A plain navigation, not window.open: Chrome on iPhone can ignore window.open
  // once the tap is a few awaits back, leaving Checkout spinning (2026-11-02).
  window.location.assign(bridgeUrl);
};

// Keep this for backward compatibility if needed elsewhere
export const fetchProduct = async (productId) => {
  const FETCH_PRODUCT_BY_ID_QUERY = `
    query getProduct($id: ID!) {
      product(id: $id) {
        id
        title
        description
        priceRange {
          minVariantPrice {
            amount
            currencyCode
          }
        }
        images(first: 5) {
          edges {
            node {
              url
              altText
            }
          }
        }
        variants(first: 10) {
          edges {
            node {
              id
              title
              price {
                amount
                currencyCode
              }
              compareAtPrice {
                amount
                currencyCode
              }
              availableForSale
            }
          }
        }
      }
    }
  `;

  try {
    if (!productId) {
      throw new Error('Product ID is required');
    }

    const response = await fetch(SHOPIFY_GRAPHQL_URL, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-Shopify-Storefront-Access-Token': SHOPIFY_CONFIG.storefrontAccessToken,
      },
      body: JSON.stringify({
        query: FETCH_PRODUCT_BY_ID_QUERY,
        variables: { id: productId }
      }),
      // Checkout waits on this, so it can't be allowed to hang.
      signal: AbortSignal.timeout?.(PRODUCT_FETCH_TIMEOUT_MS),
    }).catch((error) => {
      if (error?.name === 'TimeoutError') throw new Error('Shopify took too long to answer');
      throw error;
    });

    const data = await response.json();
    
    console.log('️ Shopify Product Response:', data);
    
    if (data.errors) {
      console.error(' Shopify API Error:', data.errors);
      throw new Error(data.errors[0]?.message || 'Failed to fetch product');
    }

    return data.data.product;
  } catch (error) {
    console.error(' Error fetching Shopify product:', error);
    throw error;
  }
};

export const fetchShopifyOrders = async (customerAccessToken) => {
  const FETCH_CUSTOMER_ORDERS_QUERY = `
    query getCustomerOrders($customerAccessToken: String!) {
      customer(customerAccessToken: $customerAccessToken) {
        orders(first: 50, sortKey: PROCESSED_AT, reverse: true) {
          edges {
            node {
              id
              orderNumber
              processedAt
              financialStatus
              fulfillmentStatus
              totalPrice {
                amount
                currencyCode
              }
              lineItems(first: 10) {
                edges {
                  node {
                    title
                    quantity
                    originalTotalPrice {
                      amount
                    }
                    variant {
                      image {
                        url
                      }
                      product {
                        title
                      }
                    }
                  }
                }
              }
            }
          }
        }
      }
    }
  `;

  try {
    if (!customerAccessToken) {
      throw new Error('Customer access token is required');
    }

    const response = await fetch(SHOPIFY_GRAPHQL_URL, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-Shopify-Storefront-Access-Token': SHOPIFY_CONFIG.storefrontAccessToken,
      },
      body: JSON.stringify({ 
        query: FETCH_CUSTOMER_ORDERS_QUERY,
        variables: { customerAccessToken }
      }),
    });

    const data = await response.json();
    
    if (data.errors) {
      console.error(' Shopify API Error:', data.errors);
      throw new Error(data.errors[0]?.message || 'Failed to fetch orders');
    }

    if (!data.data?.customer) {
      return []; // No customer or orders found
    }

    return data.data.customer.orders.edges.map(edge => edge.node);
  } catch (error) {
    console.error(' Error fetching Shopify orders:', error);
    throw error;
  }
};
